import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronUp, FaHandsHelping, FaLock } from 'react-icons/fa';
import { getSubjectShortLabel } from '../../utils/dseSubjects';
import { getJupasOrderBandColor } from '../../utils/jupasOrderDisplay';
import {
  parseJupasRankingText,
  parseSurveyResponseScores,
} from '../../utils/jupasSurveyResultsDisplay';
import { requiresInterestLowHelpReason } from '../../utils/jupasSurveySubmitPayload';

function ScoreGradeTile({ code, score, t }) {
  return (
    <div className="flex min-w-0 flex-col items-center rounded-xl border border-edge/80 bg-gradient-to-b from-surface to-surface-muted/50 px-1.5 py-2 text-center shadow-sm">
      <span className="truncate text-[10px] font-bold uppercase tracking-wider text-link">
        {getSubjectShortLabel(t, code)}
      </span>
      <span className="mt-1 whitespace-nowrap text-lg font-bold tabular-nums leading-none text-ink">
        {score || '—'}
      </span>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <section className="overflow-hidden rounded-xl border border-edge bg-surface shadow-sm">
      <header className="border-b border-edge bg-surface-muted/50 px-3 py-2">
        <h4 className="text-sm font-bold text-ink">{title}</h4>
      </header>
      <div className="p-3">{children}</div>
    </section>
  );
}

function ReviewField({ label, value, className = '' }) {
  return (
    <div className={className}>
      <p className="text-[11px] font-medium text-ink-subtle">{label}</p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm font-medium leading-relaxed text-ink">
        {value || '—'}
      </p>
    </div>
  );
}

function FeedbackRatingBadge({ value }) {
  if (value == null || value === '') return <span className="text-ink-subtle">—</span>;
  return (
    <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-lg border border-primary/30 bg-primary/10 px-1.5 text-sm font-bold tabular-nums text-primary">
      {value}
    </span>
  );
}

function formatYesNo(value, t) {
  if (value === 'yes') return t('jupasListSubmission.comment.yes');
  if (value === 'no') return t('jupasListSubmission.comment.no');
  return '—';
}

function ScoresDisplay({ row, t }) {
  const { core, electives, spnBonus, spnBonusDetails } = parseSurveyResponseScores(row);

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
          {t('jupasListSubmission.submit.coreHeading')}
        </p>
        <div className="grid grid-cols-4 gap-1.5">
          {core.map((item) => (
            <ScoreGradeTile key={item.subjectCode} code={item.subjectCode} score={item.score} t={t} />
          ))}
        </div>
      </div>

      {electives.length > 0 ? (
        <div>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            {t('jupasListSubmission.submit.electivesHeading')}
          </p>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
            {electives.map((item, index) => (
              <ScoreGradeTile
                key={`${item.subjectCode}-${index}`}
                code={item.subjectCode || '—'}
                score={item.score}
                t={t}
              />
            ))}
          </div>
        </div>
      ) : null}

      {spnBonus === 'yes' ? (
        <div className="rounded-xl border border-edge/80 bg-gradient-to-b from-surface to-surface-muted/50 px-3 py-2.5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            {t('jupasListSubmission.submit.spnBonusHeading')}
          </p>
          <p className="mt-1 text-sm font-medium text-ink">{spnBonusDetails || t('jupasListSubmission.comment.yes')}</p>
        </div>
      ) : null}
    </div>
  );
}

function RankingDisplay({ row, t }) {
  const ranking = parseJupasRankingText(row.jupasRanking);
  const admittedIndex = ranking.length > 0 ? ranking.length - 1 : -1;

  if (!ranking.length) {
    return <p className="text-sm text-ink-subtle">{t('jupasSurveyResults.noRankingData')}</p>;
  }

  return (
    <ol className="space-y-1.5">
      {ranking.map((entry, index) => {
        const bandIndex = entry.index >= 0 ? entry.index : index;
        const isAdmitted = index === admittedIndex;
        return (
          <li
            key={`${entry.rankLabel}-${entry.code}-${index}`}
            className={`flex select-none items-stretch gap-2 rounded-lg border px-2 py-1.5 ${
              isAdmitted
                ? 'border-emerald-300/80 bg-emerald-50/60 dark:border-emerald-800/50 dark:bg-emerald-950/30'
                : 'border-edge bg-surface-muted/30'
            }`}
          >
            <span
              className="flex h-6 w-6 shrink-0 self-center items-center justify-center rounded-full text-[9px] font-bold leading-none text-neutral-900"
              style={{ backgroundColor: getJupasOrderBandColor(bandIndex) }}
            >
              {entry.rankLabel}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="shrink-0 text-[9px] font-bold leading-none text-link">{entry.code}</span>
                {isAdmitted ? (
                  <span className="ml-auto shrink-0 rounded-full bg-emerald-600 px-1.5 py-px text-[8px] font-bold leading-tight text-white">
                    {t('jupasListSubmission.admittedLabel')}
                  </span>
                ) : null}
              </div>
              {entry.title ? (
                <p className="mt-px truncate text-[11px] font-medium leading-tight text-ink">{entry.title}</p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function ResponseBadge({ tone, children }) {
  const tones = {
    neutral: 'border-edge bg-canvas text-ink-muted',
    primary: 'border-blue-100 bg-accent/25 text-link dark:border-blue-900/40',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-200',
    warning: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
}

export default function JupasSurveyResponseDetail({
  row,
  expanded,
  onToggle,
  formatDateTime,
}) {
  const { t } = useTranslation();
  const institutionLabel = t(`jupasSurvey.institutions.${row.institution}`, {
    defaultValue: row.institution,
  });
  const displayUser = row.userId || t('jupasSurveyResults.anonymous');

  return (
    <article className="overflow-hidden rounded-2xl border border-edge/80 bg-surface/95 shadow-sm backdrop-blur-sm transition-shadow hover:shadow-md">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-surface-muted/40"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="font-semibold text-ink break-all">{displayUser}</p>
            {(row.isAnonymous || !row.userId) ? (
              <ResponseBadge tone="neutral">{t('jupasSurveyResults.anonymousBadge')}</ResponseBadge>
            ) : null}
            <ResponseBadge tone="primary">{row.jupasCode}</ResponseBadge>
            <ResponseBadge tone="neutral">{institutionLabel}</ResponseBadge>
            {row.offerRanking ? (
              <ResponseBadge tone="neutral">{row.offerRanking}</ResponseBadge>
            ) : null}
            {row.volunteerWilling ? (
              <ResponseBadge tone="success">{t('jupasSurveyResults.volunteerBadge')}</ResponseBadge>
            ) : null}
            {row.isInternalAccountInput ? (
              <ResponseBadge tone="warning">{t('jupasSurveyResults.internalAccountBadge')}</ResponseBadge>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-ink-muted">{formatDateTime(row.updatedAt || row.createdAt)}</p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-edge bg-surface px-2.5 py-1 text-xs font-semibold text-ink-muted">
          {expanded ? (
            <>
              {t('jupasSurveyResults.hide')}
              <FaChevronUp className="h-3 w-3" aria-hidden />
            </>
          ) : (
            <>
              {t('jupasSurveyResults.view')}
              <FaChevronDown className="h-3 w-3" aria-hidden />
            </>
          )}
        </span>
      </button>

      {expanded ? (
        <div className="space-y-3 border-t border-edge px-3 py-3 sm:px-4 sm:py-4">
          <DetailSection title={t('jupasListSubmission.stepScore')}>
            <ScoresDisplay row={row} t={t} />
          </DetailSection>

          <DetailSection title={t('jupasListSubmission.stepRanking')}>
            <RankingDisplay row={row} t={t} />
          </DetailSection>

          <DetailSection title={t('jupasListSubmission.stepComment')}>
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <ReviewField
                  label={t('jupasListSubmission.comment.usedCalculator')}
                  value={formatYesNo(row.usedCalculator, t)}
                />
                {row.usedCalculator === 'yes' ? (
                  <div>
                    <p className="text-[11px] font-medium text-ink-subtle">
                      {t('jupasListSubmission.comment.calculatorHelpfulness')}
                    </p>
                    <div className="mt-1">
                      <FeedbackRatingBadge value={row.calculatorHelpfulness} />
                    </div>
                  </div>
                ) : null}
                <ReviewField
                  label={t('jupasListSubmission.comment.usedInterestTest')}
                  value={formatYesNo(row.usedInterestTest, t)}
                />
                {row.usedInterestTest === 'yes' ? (
                  <>
                    <div>
                      <p className="text-[11px] font-medium text-ink-subtle">
                        {t('jupasListSubmission.comment.interestHelpfulness')}
                      </p>
                      <div className="mt-1">
                        <FeedbackRatingBadge value={row.interestHelpfulness} />
                      </div>
                    </div>
                    {requiresInterestLowHelpReason(row.interestHelpfulness) && row.interestLowHelpReason ? (
                      <ReviewField
                        className="sm:col-span-2"
                        label={t('jupasListSubmission.comment.interestLowHelpReason')}
                        value={[
                          t(`jupasListSubmission.comment.interestLowHelpReasons.${row.interestLowHelpReason}`),
                          row.interestLowHelpReason === 'other' ? row.interestLowHelpReasonOther : null,
                        ]
                          .filter(Boolean)
                          .join(' — ')}
                      />
                    ) : null}
                    <div>
                      <p className="text-[11px] font-medium text-ink-subtle">
                        {t('jupasListSubmission.comment.interestAccuracy')}
                      </p>
                      <div className="mt-1">
                        <FeedbackRatingBadge value={row.interestAccuracy} />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
              {(row.improvements || row.freeformFeedback) ? (
                <div className="grid gap-3 border-t border-edge pt-3 sm:grid-cols-2">
                  <ReviewField
                    label={t('jupasListSubmission.comment.improvements')}
                    value={row.improvements}
                  />
                  <ReviewField
                    label={t('jupasListSubmission.comment.freeform')}
                    value={row.freeformFeedback}
                  />
                </div>
              ) : null}
            </div>
          </DetailSection>

          {row.volunteerWilling ? (
            <section className="overflow-hidden rounded-xl border border-emerald-200/80 bg-emerald-50/50 shadow-sm dark:border-emerald-900/40 dark:bg-emerald-950/25">
              <header className="flex items-center gap-2 border-b border-emerald-200/80 px-3 py-2 dark:border-emerald-900/40">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                  <FaHandsHelping className="h-3.5 w-3.5" aria-hidden />
                </span>
                <h4 className="text-sm font-bold text-emerald-950 dark:text-emerald-100">
                  {t('jupasSurveyResults.volunteerHeading')}
                </h4>
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-surface/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
                  <FaLock className="h-2.5 w-2.5" aria-hidden />
                  {t('jupasSurveyResults.presenterOnly')}
                </span>
              </header>
              <div className="grid gap-2 p-3 text-sm sm:grid-cols-2">
                <ReviewField label={t('jupasSurvey.fields.volunteerName')} value={row.volunteerName} />
                <ReviewField label={t('jupasSurvey.fields.volunteerContact')} value={row.volunteerContact} />
                <ReviewField
                  className="sm:col-span-2"
                  label={t('jupasSurvey.fields.volunteerProgramme')}
                  value={row.volunteerProgramme}
                />
              </div>
            </section>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
